import React, { Component } from 'react';
import classnames from 'classnames';
import { IABSUploadFileProps, IFileCodeList } from '../interface';
import { Upload } from 'antd';
import ABSMessage from '../../ABSMessage';
import { UploadFileType } from '../utils';
import ABSIconText from '../../ABSIconText';
import ABSProgressBar from '../../ABSProgressBar';
import commonUtils from '../../../utils/commonUtils';
import { GlobalApi } from '../../../abs/config/api';
import ABSFileTypeText, { fileType } from '../../ABSFileTypeText';
import './index.less';

interface IFileState {
  fileList: any[];
  progressValue: number;
}

export const FileType = fileType;

export default class File extends Component<IABSUploadFileProps, IFileState> {
  /** 上传进度，取值为 0~100（目前仅对上传单个文件有效） */
  static defaultProps = {
    icon: 'attachment',
    iconText: '添加附件',
    multiple: false,
    format: 'fileCodeList'
  };

  isCanUpload = true;

  public percent: number;

  constructor(props: any) {
    super(props);
    this.percent = 0;
    this.state = {
      fileList: [],
      progressValue: this.percent
    };
  }

  componentDidMount() {
    const { fileList, resetResult } = this.props;
    if (Array.isArray(fileList)) {
      this.setState({ fileList: fileList });
    }
    if (resetResult) {
      resetResult();
    }
  }

  componentDidUpdate(prevProps: IABSUploadFileProps) {
    const { fileList, resetResult } = this.props;
    if (!commonUtils.isEqual(fileList, prevProps.fileList)) {
      this.setState({ fileList: fileList ? fileList : [] });
      if (resetResult) {
        resetResult();
      }
    }
  }

  renderProps() {
    const { multiple, action, limitSize, limitType, withCredentials } = this.props;
    return {
      multiple: multiple,
      showUploadList: false,
      action: action,
      withCredentials: withCredentials,
      beforeUpload: file => {
        this.isCanUpload = true;
        if (limitType && limitType.length > 0) {
          const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
          const isIn = limitType.indexOf(ext) >= 0;
          if (!isIn) {
            ABSMessage.error(`上传文件的类型不对，应该是${limitType.join()}`);
            this.isCanUpload = false;
            return false;
          }
        }
        if (limitSize) {
          const { fileList } = this.state;
          let sizeTemp = 0;
          fileList.map((item: any) => {
            const { size } = item ? item : 0;
            sizeTemp += size;
          });
          const isMore = (file.size + sizeTemp) / (1024 * 1024) < limitSize;
          if (!isMore) {
            ABSMessage.error(`上传文件的大小不能超过${limitSize}MB`);
            this.isCanUpload = false;
            return false;
          }
        }
        return action ? true : false;
      }
    };
  }

  renderFileList() {
    const { fileList } = this.state;
    return fileList.map((item, index) => {
      const { name, size, code } = item ? item : '';
      const clazz = code ? 'upload-file-download' : 'upload-file';
      return (
        <ABSFileTypeText
          className={clazz}
          key={index}
          name={name}
          size={size}
          iconSize={32}
          onDownload={() => this.onDownloadFile(code)}
          onDelete={e => this.onDelete(index, e)}
        />
      );
    });
  }

  renderProgress() {
    const { progressValue } = this.state;
    if (progressValue <= 0 || progressValue >= 100) {
      return null;
    }
    const percent = Math.round(progressValue);
    return <ABSProgressBar percent={percent} status="active" />;
  }

  render() {
    const { className, style, icon, iconText, redDesc, multiple, limitType } = this.props;
    const { fileList } = this.state;
    const clazs = classnames('abs-upload-file', className);
    const disabled = fileList.length === 1 && !multiple ? true : false;
    const iconTextClassName = disabled ? 'upload-icon-disabled' : 'upload-icon';
    return (
      <div className={clazs} style={style}>
        <div>
          <Upload
            {...this.renderProps()}
            disabled={disabled}
            onChange={this.onChange}
            accept={limitType === UploadFileType.video ? 'video/*' : undefined}
          >
            <div className={iconTextClassName}>
              <ABSIconText icon={icon as any} text={iconText as any} />
            </div>
          </Upload>
          <span className="upload-red-desc">{redDesc}</span>
        </div>
        {this.renderFileList()}
        {this.renderProgress()}
      </div>
    );
  }

  reset = () => {
    const { fileList } = this.props;
    const defaultFile = fileList ? fileList : [];
    this.setState({ fileList: defaultFile });
  };

  getValue = () => {
    const { fileList } = this.state;
    const { format } = this.props;
    let result;
    switch (format) {
      case 'fileCodeList':
        result = this.getCodeList();
        break;
      case 'fileList':
        result = fileList ? fileList : [];
        break;
      case 'formData':
        result = this.getFormData();
        break;
      default:
        result = this.getCodeList();
    }
    return result;
  };

  getFormData() {
    const { fileList } = this.state;
    if (!fileList || fileList.length < 1) {
      return null;
    }
    const formData = new FormData();
    fileList.forEach(file => {
      formData.append('file', file);
    });
    return formData;
  }

  getCodeList() {
    const { fileList } = this.state;
    let codeList: IFileCodeList[] = [];
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        const { code = '', name = '', size = 0 } = fileList[i];
        codeList.push({ code, name, size });
      }
    }
    return codeList;
  }

  onChange = (info: any) => {
    if (!this.isCanUpload) {
      return;
    }
    const { multiple, action } = this.props;
    const { event } = info;
    if (event && !multiple) {
      const { percent } = event;
      if (!isNaN(percent)) {
        this.percent = percent;
        this.setState({ progressValue: this.percent });
      }
    }
    const { file } = info;
    // 不用globalApi
    if (!action) {
      this.setState(({ fileList }) => {
        const newFileList = fileList.slice();
        newFileList.push(file);
        this.onValidator(newFileList);
        return { fileList: newFileList };
      });
      return;
    }
    // 用globalApi
    if (file.status === 'uploading') {
      return;
    }
    if (file.status === 'done') {
      if (!file.response || !Array.isArray(file.response)) {
        return;
      }
      let newfile: any = {};
      // tslint:disable-next-line:forin
      for (const key in file) {
        if (key !== 'response') {
          newfile[key] = file[key];
        } else {
          newfile.code = file.response[0].FileCode;
        }
      }
      this.setState(({ fileList }) => {
        const newFileList = fileList.slice();
        newFileList.push(newfile);
        this.onValidator(newFileList);
        return { fileList: newFileList };
      });
    } else if (info.file.status === 'error') {
      ABSMessage.error(`${info.file.name}上传失败！`);
    }
  };

  onDownloadFile = (code: string) => {
    if (code) {
      commonUtils.downloadFile(`${GlobalApi.downloadFiles}${code}`);
    }
  };

  onDelete = (index: number, e?: any) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    const { onDelete } = this.props;
    this.setState(({ fileList }) => {
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      this.onValidator(newFileList);
      return {
        fileList: newFileList
      };
    });
    if (onDelete) {
      onDelete();
    }
  };

  onValidator = data => {
    const { onValidator } = this.props;
    if (onValidator) {
      onValidator(data);
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ABSUpload/File/index.tsx